import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Dropcursor from '@tiptap/extension-dropcursor';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import Underline from '@tiptap/extension-underline';
import FontFamily from '@tiptap/extension-font-family';
import TextStyle from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import Iframe from './custom-nodes/iframe';

window.Editor = Editor;
window.StarterKit = StarterKit;
window.Document = Document;
window.Paragraph = Paragraph;
window.Text = Text;
window.TextAlign = TextAlign;
window.Highlight = Highlight;
/* window.Image = Image; */
window.Dropcursor = Dropcursor;
window.Table = Table;
window.TableRow = TableRow;
window.TableCell = TableCell;
window.TableHeader = TableHeader;
window.BubbleMenu = BubbleMenu;
window.Underline = Underline;
window.FontFamily = FontFamily;
window.TextStyle = TextStyle;
window.Link = Link;
window.Iframe = Iframe;

document.addEventListener('alpine:init', () => {
    Alpine.data('editor', (content, key, canEdit = true) => {
        let editor;

        return {
            updatedAt: Date.now(),
            showLinkDialog: false,
            linkAction: '',
            linkText: '',
            linkFrom: 0,
            linkTo: 0,
            showEmbedDialog: false,
            showImageDialog: false,
            imageUrl: '',
            embedError: '',
            embedCode: '',
            embedFrom: 0,
            embedTo: 0,
            init() {
                const _this = this;

                editor = new Editor({
                    element: this.$refs.editorReference,
                    extensions: [
                        StarterKit,
                        TextAlign.configure({
                            types: ['heading', 'paragraph'],
                        }),
                        Highlight,
                        Image.configure({
                            HTMLAttributes: {
                                class: 'mx-auto rounded-lg',
                            },
                        }),
                        Table.configure({
                            resizable: true,
                        }),
                        TableRow,
                        TableHeader,
                        TableCell,
                        BubbleMenu.configure({
                            element: document.querySelector('.menu'),
                        }),
                        Underline,
                        FontFamily,
                        TextStyle,
                        Link.configure({
                            openOnClick: false,
                            HTMLAttributes: {
                                class: 'tx-primary',
                            },
                        }),
                        Iframe,
                    ],
                    editorProps: {
                        attributes: {
                            class: 'prose prose-indigo bg-white prose-lg p-5 max-w-full focus:outline-none',
                        },
                    },
                    content: content,
                    editable: canEdit,
                    onUpdate({ editor }) {
                        _this.content = editor.getHTML();
                    },
                });
                window.addEventListener('editor:setContent', (event) => {
                    editor
                        .chain()
                        .setContent(event.detail.content)
                        .focus()
                        .run();
                    window.Livewire.emit(
                        'editor:getContent',
                        event.detail.content
                    );
                });

                window.addEventListener('editor:clearContent', (event) => {
                    editor.chain().clearContent().focus().run();
                });

                window.addEventListener('gallery:insertImage', (event) => {
                    if (event.detail.url && event.detail.frameID === key) {
                        editor
                            .chain()
                            .focus()
                            .setImage({ src: event.detail.url })
                            .run();
                    }
                });

                editor.on('selectionUpdate', () => {
                    this.$wire.call('$refresh')
                })
            },

            isActive(type, opts = {}) {
                return editor.isActive(type, opts);
            },
            toggleBold() {
                editor.chain().toggleBold().focus().run();
            },
            toggleItalic() {
                editor.chain().toggleItalic().focus().run();
            },
            toggleUnderline() {
                editor.chain().focus().toggleUnderline().run();
            },
            toggleStrike() {
                editor.chain().toggleStrike().focus().run();
            },
            toggleCode() {
                editor.chain().toggleCode().focus().run();
            },
            setTextAlign(position) {
                editor.chain().setTextAlign(position).focus().run();
            },
            toggleHighlight() {
                editor.chain().toggleHighlight().focus().run();
            },
            toggleHeading(level) {
                editor.chain().toggleHeading({ level: level }).focus().run();
            },
            setParagraph() {
                editor.chain().setParagraph().focus().run();
            },
            setFontFamily(font) {
                editor.chain().focus().setFontFamily(font).run();
            },
            unsetFontFamily() {
                editor.chain().focus().unsetFontFamily().run();
            },
            toggleBulletList() {
                editor.chain().toggleBulletList().focus().run();
            },
            toggleOrderedList() {
                editor.chain().toggleOrderedList().focus().run();
            },
            toggleCodeBlock() {
                editor.chain().toggleCodeBlock().focus().run();
            },
            toggleBlockquote() {
                editor.chain().toggleBlockquote().focus().run();
            },
            setHorizontalRule() {
                editor.chain().setHorizontalRule().focus().run();
            },
            setHardBreak() {
                editor.chain().setHardBreak().focus().run();
            },
            removeFormat() {
                editor.chain().clearNodes().focus().run();
                editor.chain().unsetAllMarks().focus().run();
            },
            insertTable() {
                editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run();
            },
            addColumnBefore() {
                editor.chain().focus().addColumnBefore().run();
            },
            addColumnAfter() {
                editor.chain().focus().addColumnAfter().run();
            },
            deleteColumn() {
                editor.chain().focus().deleteColumn().run();
            },
            addRowBefore() {
                editor.chain().focus().addRowBefore().run();
            },
            addRowAfter() {
                editor.chain().focus().addRowAfter().run();
            },
            deleteRow() {
                editor.chain().focus().deleteRow().run();
            },
            deleteTable() {
                editor.chain().focus().deleteTable().run();
            },
            mergeCells() {
                editor.chain().focus().mergeCells().run();
            },
            splitCell() {
                editor.chain().focus().splitCell().run();
            },
            toggleHeaderColumn() {
                editor.chain().focus().toggleHeaderColumn().run();
            },
            toggleHeaderRow() {
                editor.chain().focus().toggleHeaderRow().run();
            },
            toggleHeaderCell() {
                editor.chain().focus().toggleHeaderCell().run();
            },
            undo() {
                editor.chain().undo().focus().run();
            },
            redo() {
                editor.chain().redo().focus().run();
            },
            link() {
                editor.commands.extendMarkRange('link');

                this.linkFrom = editor.view.state.selection.from;
                this.linkTo = editor.view.state.selection.to;
                this.linkAction = editor.getAttributes('link').href ?? '';
                this.linkText = editor.view.state.doc.textBetween(
                    this.linkFrom,
                    this.linkTo
                );

                this.showLinkDialog = true;
            },
            setLink() {
                if (!this.linkText) {
                    this.linkText = this.linkAction;
                }

                editor.commands.focus();

                editor.view.dispatch(
                    editor.view.state.tr.insertText(this.linkText)
                );

                editor
                    .chain()
                    .setTextSelection({
                        from: this.linkFrom,
                        to: this.linkFrom + this.linkText.length,
                    })
                    .setLink({ href: this.linkAction, target: '_blank' })
                    .run();

                this.cancelLink();
            },
            cancelLink() {
                this.linkText = this.linkAction = '';
                this.linkFrom = this.linkTo = 0;

                this.showLinkDialog = false;
            },
            embed() {
                this.embedFrom = editor.view.state.selection.from;
                this.embedTo = editor.view.state.selection.to;

                this.embedError = '';
                this.showEmbedDialog = true;
            },
            addEmbed() {
                const element = this.parseHTMLString(this.embedCode);
                if (!element) {
                    this.embedError = 'Invalid embed code';
                    return;
                }
                const srcAttribute = element.src ? element.src : null;

                if (srcAttribute) {
                    editor
                        .chain()
                        .focus()
                        .setTextSelection({
                            from: this.embedFrom,
                            to: this.embedTo,
                        })
                        .setIframe({ src: srcAttribute.value })
                        .run();
                } else {
                    this.embedError = 'Invalid embed code';
                    return;
                }

                this.cancelEmbed();
            },
            image() {
                this.showImageDialog = !this.showImageDialog;
            },
            addImage() {
                const url = this.imageUrl;

                if (url) {
                    editor.chain().focus().setImage({ src: url }).run();
                }

                this.imageUrl = '';
                this.showImageDialog = false;
            },
            cancelEmbed() {
                this.embedCode = '';
                this.embedFrom = this.embedTo = 0;

                this.showEmbedDialog = false;
            },
            parseHTMLString(string) {
                const container = document.createElement('div');
                container.innerHTML = string;

                const element = container.childNodes[0];

                return element.attributes;
            },
        };
    });
});
